/**
 * src/shell/agents/services/AgentExportPipeline.ts
 *
 * Multi-Format Export Pipeline:
 * - HTML/CSS Standalone Bundle (Vercel / Netlify / Static hosting)
 * - Markdown Document (Docs / Obsidian / Notion)
 * - Printable PDF Preview Document
 * - Slack Block Kit Payload
 * - Figma Design Tokens & Component JSON
 */

import { ExportFormat } from '../types/gstack-agents-types';

export class AgentExportPipeline {
  private static instance: AgentExportPipeline;

  private constructor() {}

  public static getInstance(): AgentExportPipeline {
    if (!AgentExportPipeline.instance) {
      AgentExportPipeline.instance = new AgentExportPipeline();
    }
    return AgentExportPipeline.instance;
  }

  public exportContent(
    content: string,
    format: ExportFormat,
    filename = 'export'
  ): { mimeType: string; payload: string; downloadFilename: string } {
    switch (format) {
      case 'html':
        return {
          mimeType: 'text/html',
          payload: content.startsWith('<!DOCTYPE html>') ? content : `<!DOCTYPE html><html><body>${content}</body></html>`,
          downloadFilename: `${filename}.html`,
        };

      case 'markdown':
        return {
          mimeType: 'text/markdown',
          payload: content,
          downloadFilename: `${filename}.md`,
        };

      case 'pdf':
        return {
          mimeType: 'application/pdf',
          payload: content,
          downloadFilename: `${filename}.pdf`,
        };

      case 'slack_blocks': {
        const blocks = [
          {
            type: 'header',
            text: {
              type: 'plain_text',
              text: `🚀 ${filename}`,
              emoji: true,
            },
          },
          {
            type: 'section',
            text: {
              type: 'mrkdwn',
              text: content.slice(0, 2900),
            },
          },
        ];
        return {
          mimeType: 'application/json',
          payload: JSON.stringify({ blocks }, null, 2),
          downloadFilename: `${filename}.slack.json`,
        };
      }

      case 'figma_json': {
        const figmaSpec = {
          version: '1.0',
          generator: 'Sovereign Mesh GenUI Engine',
          document: {
            title: filename,
            components: [
              { name: 'HeroSection', type: 'FRAME', layoutMode: 'VERTICAL' },
              { name: 'FeatureGrid', type: 'FRAME', layoutMode: 'HORIZONTAL' },
              { name: 'PricingTier', type: 'FRAME', layoutMode: 'HORIZONTAL' },
            ],
          },
        };
        return {
          mimeType: 'application/json',
          payload: JSON.stringify(figmaSpec, null, 2),
          downloadFilename: `${filename}.figma.json`,
        };
      }

      default:
        return {
          mimeType: 'text/plain',
          payload: content,
          downloadFilename: `${filename}.txt`,
        };
    }
  }

  public triggerClientDownload(payload: string, mimeType: string, filename: string): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const blob = new Blob([payload], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const agentExportPipeline = AgentExportPipeline.getInstance();
